/**
 * The calls ringing the tech's phone (Idea #86; ADR 0137): every tech whose
 * phone rings sees who's calling before anyone answers. The number, the client
 * it matches with Managed or Hourly, and the contact's name; on a colleague's
 * call, their name and the caller they're on a call with (a blind transfer or a
 * question, while it rings). A card each, above the call card in the corner.
 *
 * Checked every 2 seconds while the tab is visible: a ring lasts seconds. When
 * a ring stops, the call card is checked at once, so whoever answered sees
 * their call without waiting for its own 5 seconds.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type { RingingCall } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Badge, HipaaBadge } from "../../components/ui/Badge";
import { formatPhone } from "../../lib/phones";

const POLL_MS = 2_000;
const BILLING: Record<"managed" | "hourly", string> = { managed: "Managed", hourly: "Hourly" };

/** Who's calling, as the card's heading and its name say it. */
function caller(ring: RingingCall): string {
  if (ring.direction === "internal" && ring.colleague) {
    return ring.colleague;
  }
  return ring.number === null ? "an unknown number" : formatPhone(ring.number);
}

/** What a screen reader hears once, as the ring starts. */
function announcement(ring: RingingCall): string {
  const client = ring.client ? `, ${ring.client.name}` : "";
  return `Incoming call from ${caller(ring)}${client}`;
}

function RingCard({ ring }: { ring: RingingCall }) {
  const who = caller(ring);
  const fromColleague = ring.direction === "internal";
  const onACall = fromColleague && ring.number !== null;
  return (
    <section
      role="region"
      aria-label={`Incoming call from ${who}`}
      className="flex flex-col overflow-hidden rounded-panel border border-brand bg-panel shadow-[0_14px_30px_var(--brand-shadow)]"
    >
      {/* The call card's Axon blue band (ADR 0049): a ringing phone stands out too. */}
      <div
        data-part="band"
        className="bg-brand bg-[radial-gradient(90%_130%_at_100%_0%,var(--hero-glow),transparent_60%)] px-4 py-3 text-white"
      >
        <p className="flex items-center gap-1.5 text-[11.5px] font-semibold tracking-wide uppercase">
          <span aria-hidden="true" className="size-2 rounded-pill bg-white motion-safe:animate-ping" />
          Incoming call
        </p>
        <h2 className="mt-0.5 text-[16px] font-bold tracking-tight">{who}</h2>
        {onACall ? (
          <p className="text-[13px] text-white/90">
            {`On a call with ${ring.number === null ? "" : formatPhone(ring.number)}`}
          </p>
        ) : null}
      </div>
      {ring.client ? (
        <div className="flex flex-col gap-1.5 px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="text-[14px] font-semibold">{ring.client.name}</p>
            {ring.client.billing_type ? (
              <Badge tone="neutral">{BILLING[ring.client.billing_type]}</Badge>
            ) : null}
            {ring.client.hipaa ? <HipaaBadge /> : null}
          </div>
          {ring.contact_name ? <p className="text-[13px] text-muted">{ring.contact_name}</p> : null}
        </div>
      ) : fromColleague && !onACall ? null : (
        <p className="px-4 py-3 text-[13px] text-muted">No client matches this number</p>
      )}
    </section>
  );
}

export function RingingCalls() {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["ringing-calls"],
    queryFn: () => api.getRingingCalls(),
    refetchInterval: POLL_MS,
    // Only while the tab is visible.
    refetchIntervalInBackground: false,
    retry: false,
  });
  const rings = query.data ?? [];
  const ids = rings.map((r) => r.call_id).join(" ");
  const before = useRef(ids);

  useEffect(() => {
    const gone = before.current.split(" ").some((id) => id !== "" && !ids.split(" ").includes(id));
    before.current = ids;
    if (gone) {
      // Answered, maybe by this tech: their call card shouldn't wait.
      void queryClient.invalidateQueries({ queryKey: ["active-call"] });
    }
  }, [ids, queryClient]);

  // Always on the page, so a ring is announced as it starts; the newest one,
  // whose words don't change while it rings, so it's heard once (#249).
  const newest = rings.at(-1);
  return (
    <>
      <p data-part="ring-status" role="status" className="sr-only">
        {newest ? announcement(newest) : ""}
      </p>
      {rings.map((ring) => (
        <RingCard key={ring.call_id} ring={ring} />
      ))}
    </>
  );
}
